import React, { useMemo } from 'react';
import * as THREE from 'three';
import { RolledSteelEqualAngleSection } from '../../types/beamSection';

/**
 * Rolled Steel Equal Angle Section Component
 * Renders an engineering-accurate L-Angle cross-section along the beam
 * 
 * EQUAL ANGLE GEOMETRY (L-shaped profile):
 * 
 *        ← A (Leg length) →
 *        ┌─────────────────┐  ↑
 *        │  ← t →          │  │
 *        ├─────────────────┤  │
 *        │                    │ B (Leg length)
 *        │  ┌─── r1           │ (A = B for equal angles)
 *        │  │                 │
 *        │  │                 │
 *        └──┘                 ↓
 *        
 *        Centroid at (Cxx, Cyy) from back edges
 * 
 * IMPORTANT: Matches CylinderGeometry and BoxGeometry behavior:
 * - Cross-section is in XZ plane
 * - Extrusion is along Y-axis
 * - Parent group applies quaternion rotation to align with beam direction
 * 
 * This ensures proper rendering for horizontal, vertical, and diagonal beams.
 */
interface RolledSteelEqualAngleMeshProps {
  section: RolledSteelEqualAngleSection;
  color: string;
}

export const RolledSteelEqualAngleMesh: React.FC<RolledSteelEqualAngleMeshProps> = ({ section, color }) => {
  const { A, B, t, r1, r2, length } = section;
  
  // Convert mm to meters for Three.js (assuming beam units are in meters)
  const scale = 0.09; // 1mm = 0.001m
  
  const legA = A * scale;
  const legB = B * scale;
  const thickness = t * scale;
  const rootRadius = r1 * scale;
  const toeRadius = r2 * scale;
  
  /**
   * Create L-Angle cross-section shape
   * 
   * IMPORTANT: THREE.Shape draws in XY plane, ExtrudeGeometry extrudes along Z
   * But CylinderGeometry has its axis along Y
   * 
   * Solution:
   * 1. Draw L-angle shape in XY plane (X = horizontal leg, Y = vertical leg)
   * 2. Extrude along Z for beam length
   * 3. Rotate -90° around X-axis to make Z-axis align with Y-axis
   * 4. Result: Extrusion is now along Y-axis (matches CylinderGeometry)
   */
  const angleGeometry = useMemo(() => {
    const shape = new THREE.Shape();
    
    // Draw L-angle cross-section in XY plane
    // Origin at the corner (back edges intersection)
    // X-axis = horizontal leg (right)
    // Y-axis = vertical leg (up)
    
    // Start from bottom-left corner (origin)
    // Draw the L-shape clockwise
    
    // Bottom edge of horizontal leg (from origin to right)
    shape.moveTo(0, 0);
    shape.lineTo(legA, 0);
    
    // Right edge of horizontal leg (up by thickness)
    shape.lineTo(legA, thickness);
    
    // Inner edge of horizontal leg (back to vertical leg position)
    // TODO: Implement filleted corner using rootRadius (r1) and toeRadius (r2)
    // Currently draws sharp corner - should use arc segments for realistic geometry
    shape.lineTo(thickness, thickness);
    
    // Inner edge of vertical leg (up to top)
    shape.lineTo(thickness, legB);
    
    // Top edge of vertical leg (left to back edge)
    shape.lineTo(0, legB);
    
    // Left edge (back down to origin)
    shape.lineTo(0, 0);
    
    // Extrude along Z-axis (default for ExtrudeGeometry)
    const extrudeSettings: THREE.ExtrudeGeometryOptions = {
      depth: length,  // Extrudes along Z from 0 to +length
      bevelEnabled: false,
      steps: 1,
    };
    
    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    
    // Now geometry is:
    // - Cross-section in XY plane
    // - Extruded along Z from 0 to +length
    
    // We need to:
    // 1. Rotate so extrusion is along Y-axis (like CylinderGeometry)
    // 2. Center along the extrusion axis
    
    // Rotate -90° around X-axis: converts Z-extrusion to Y-extrusion
    geometry.rotateX(-Math.PI / 2);
    
    // After rotation:
    // - Cross-section is now in XZ plane
    // - Extrusion is now along Y-axis from 0 to +length
    
    // Center on Y-axis (same as CylinderGeometry)
    geometry.translate(0, -length / 2, 0);
    
    // Center the angle at its centroid (optional - can adjust based on Cxx, Cyy if needed)
    // For now, keep corner at origin for simplicity
    
    // Final result:
    // - Cross-section in XZ plane
    // - Extruded along Y from -length/2 to +length/2
    // - Matches CylinderGeometry behavior exactly!
    
    return geometry;
  }, [legA, legB, thickness, rootRadius, toeRadius, length]);

  // Debug logging
  console.log('🔧 RolledSteelEqualAngleMesh:', {
    designation: section.designation,
    length,
    dimensions: `${A}mm × ${B}mm (A × B)`,
    thickness: `${t}mm`,
    'scaled': `${legA.toFixed(3)}m × ${legB.toFixed(3)}m`,
    'extrusion axis': 'Y-axis (after rotation)',
    'geometry span': `Y: -${(length/2).toFixed(2)} to +${(length/2).toFixed(2)}`,
    'profile': 'L-Angle (Equal)'
  });

  return (
    <mesh geometry={angleGeometry}>
      <meshStandardMaterial
        color={color}
        transparent
        opacity={0.4}
        side={THREE.DoubleSide}
        metalness={0.6}
        roughness={0.4}
      />
      {/* Wireframe overlay for better visualization */}
      <lineSegments>
        <edgesGeometry args={[angleGeometry]} />
        <lineBasicMaterial color={color} linewidth={2} />
      </lineSegments>
    </mesh>
  );
};
